import { API_BASE_URL, logging } from '@/config/env';
import type {
  SmtpConfig,
  SmtpListResponse,
  ApiSmtpListResponse,
  SmtpQueryParams,
} from './smtp.dto';

import { redirectToLogin } from '../../utils/sessionExpired';
const customLog = (...args: unknown[]) => {
  if (logging) {
    console.log(...args);
  }
};

class SmtpService {
  private baseUrl: string;
  private logoutCallback: (() => void) | null = null;

  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  setLogoutCallback(callback: () => void) {
    this.logoutCallback = callback;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    customLog('=== API Request ===');
    customLog('URL:', url);
    customLog('Method:', options.method || 'GET');

    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    customLog('Response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      customLog('Error response:', errorText);

      try {
        const errorData = JSON.parse(errorText);
        if (
          errorData.status === false &&
          errorData.message === 'Invalid or expired admin session'
        ) {
          if (this.logoutCallback) {
            this.logoutCallback();
          }
          redirectToLogin();
        }
      } catch {
        // ignore parse errors
      }

      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    customLog('Response data:', data);

    if (
      data?.status === false &&
      data?.message === 'Invalid or expired admin session'
    ) {
      if (this.logoutCallback) {
        this.logoutCallback();
      }
      redirectToLogin();
    }

    return data;
  }

  async getSmtpConfigs(
    token: string,
    params: SmtpQueryParams = {}
  ): Promise<SmtpListResponse> {
    const queryString = new URLSearchParams();
    if (params.page) queryString.append('page', params.page.toString());
    if (params.limit) queryString.append('limit', params.limit.toString());
    if (params.search) queryString.append('search', params.search);
    if (params.status !== undefined)
      queryString.append('status', params.status ? 'true' : 'false');

    const endpoint = `/admin/smtp${
      queryString.toString() ? `?${queryString.toString()}` : ''
    }`;

    const response = await this.request<ApiSmtpListResponse>(endpoint, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.data) {
      return {
        data: [],
        meta: { total: 0, page: 1, lastPage: 1, limit: params.limit || 10 },
      };
    }

    const responseData = response.data as any;

    if (Array.isArray(responseData)) {
      return {
        data: responseData,
        meta: {
          total: (response as any).total || 0,
          page: (response as any).page || 1,
          lastPage: (response as any).lastPage || 1,
          limit: params.limit || 10,
        },
      };
    }

    return {
      data: responseData.data || [],
      meta: {
        total:
          responseData.meta?.total ||
          responseData.total ||
          (response as any).total ||
          0,
        page:
          responseData.meta?.page ||
          responseData.page ||
          (response as any).page ||
          1,
        lastPage:
          responseData.meta?.lastPage ||
          responseData.totalPages ||
          (response as any).lastPage ||
          1,
        limit: params.limit || 10,
      },
    };
  }

  async getSmtpConfig(token: string, id: number): Promise<SmtpConfig> {
    const response = await this.request<{
      status: boolean;
      message: string;
      data: SmtpConfig;
    }>(`/admin/smtp/${id}`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  }

  async updateSmtpConfig(
    token: string,
    id: number,
    payload: Partial<SmtpConfig>
  ): Promise<SmtpConfig> {
    const response = await this.request<{
      status: boolean;
      message: string;
      data: SmtpConfig;
    }>(`/admin/smtp/${id}`, {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload),
    });
    return response.data;
  }
}

export const smtpService = new SmtpService();
